/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3105InitLU, U3105PayrollYearSearchRow } from "./U3105Service";

export type InitLU = U2000LUState["personalization"] &
  U3105InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3105ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3105PayrollYearSearchRow>;
  initLU: U3105InitLU;
}

interface U3105ContextProviderProps {
  initLU: U3105InitLU;
  children: ReactNode;
}

const U3105Context = React.createContext<U3105ContextType | undefined>(undefined);

export const U3105ContextProvider: React.FC<U3105ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3105",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3105PayrollYearSearchRow>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 25,
  });

  const contextValue = useMemo<U3105ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
      initLU,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers, initLU],
  );

  return <U3105Context.Provider value={contextValue}>{children}</U3105Context.Provider>;
};

export function useU3105Context(): U3105ContextType {
  const context = useContext(U3105Context);
  if (!context) {
    throw new Error("useU3105Context must be used within a U3105ContextProvider");
  }
  return context;
}
